import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BookOpen,
  Clock,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Calendar,
  Users,
  Loader2,
} from 'lucide-react';
import useFormData from '@/hooks/useFormData';
import {
  useUpadteCourseMutation,
  useGetAllCourseQuery,
} from '../../store/courseApi';
import { useSelector } from 'react-redux';
import { RootState } from '@/types';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Input } from '../ui/input';

interface FormData {
  course: string;
  cFee: string;
  cDuration: string;
  maxStudents: string;
  startDate: string;
}

const UpdatedCourse = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: allCourses, isLoading: isLoadingCourses } =
    useGetAllCourseQuery();
  const [UpadteCourse, { isLoading: isSubmitting, isError, isSuccess }] =
    useUpadteCourseMutation();

  const { formData, handleChange, resetForm, setFormData } =
    useFormData<FormData>({
      course: '',
      cFee: '',
      cDuration: '',
      maxStudents: '',
      startDate: '',
    });

  // Initial data setup using useEffect
  React.useEffect(() => {
    if (allCourses && id) {
      const currentCourse = allCourses.find((course) => course._id === id);
      if (currentCourse) {
        // Format the date if it exists
        const formatDate = (dateString: string) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        };

        setFormData({
          course: currentCourse.course || '',
          cFee: currentCourse.cFee || '',
          cDuration: currentCourse.cDuration || '',
          maxStudents: currentCourse.maxStudents?.toString() || '',
          startDate: formatDate(currentCourse.startDate) || '',
        });
      }
    }
  }, [allCourses, id, setFormData]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Format the data before submitting
    const formattedData = {
      ...formData,
      maxStudents: parseInt(formData.maxStudents) || 0,
      startDate: formData.startDate || new Date().toISOString().split('T')[0], // Ensure date is in correct format
    };

    UpadteCourse({
      Token: user?.token,
      data: formattedData,
      courseId: id,
    });
  };

  React.useEffect(() => {
    if (isSuccess && !isSubmitting) {
      toast.success('Course updated successfully');
      navigate('/courses');
      resetForm();
    } else if (isError && !isSubmitting) {
      toast.error('Failed to update course. Please try again');
    }
  }, [isSuccess, isError, isSubmitting]);

  // Loading state
  if (isLoadingCourses) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="ml-2">Loading course data...</span>
      </div>
    );
  }

  return (
    <Card className="w-full rounded-none border border-t">
      <CardHeader className="p-6 pb-0">
        <CardTitle className="text-2xl font-bold flex items-center gap-2">
          <BookOpen className="w-6 h-6" />
          <h1 style={{ letterSpacing: '3px' }}>Update Internship Course</h1>
        </CardTitle>
        <p className="mt-2">Update a course to the internship program</p>
      </CardHeader>

      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Course Name */}
            <div className="space-y-2">
              <label
                htmlFor="course"
                className="block text-sm font-medium text-muted-foreground sm:flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4 text-green-600" />
                Course Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                id="course"
                name="course"
                value={formData.course}
                required
                onChange={handleChange}
                className="w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500 transition-all border-gray-300"
                placeholder="e.g., Advanced Web Development"
              />
            </div>

            {/* Course Fee */}
            <div className="space-y-2">
              <label
                htmlFor="cFee"
                className="block text-sm font-medium text-muted-foreground sm:flex items-center gap-2"
              >
                <DollarSign className="w-4 h-4 text-green-600" />
                Course Fee <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-500">$</span>
                <Input
                  type="text"
                  id="cFee"
                  name="cFee"
                  required
                  value={formData.cFee}
                  onChange={handleChange}
                  className="w-full p-3 pl-8 border rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-transparent transition-all border-gray-300"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Course Duration */}
            <div className="space-y-2">
              <label
                htmlFor="cDuration"
                className="block text-sm font-medium text-muted-foreground sm:flex items-center gap-2"
              >
                <Clock className="w-4 h-4 text-green-600" />
                Course Duration <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                id="cDuration"
                name="cDuration"
                required
                value={formData.cDuration}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-transparent transition-all border-gray-300"
                placeholder="e.g., 12 weeks"
              />
            </div>

            {/* Max Students */}
            <div className="space-y-2">
              <label
                htmlFor="maxStudents"
                className="block text-sm font-medium text-muted-foreground sm:flex items-center gap-2"
              >
                <Users className="w-4 h-4 text-green-600" />
                Maximum Students
              </label>
              <Input
                type="number"
                id="maxStudents"
                name="maxStudents"
                value={formData.maxStudents}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-transparent transition-all border-gray-300"
                placeholder="e.g., 30"
              />
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <label
                htmlFor="startDate"
                className="block text-sm font-medium text-muted-foreground sm:flex items-center gap-2"
              >
                <Calendar className="w-4 h-4 text-green-600" />
                Start Date
              </label>
              <Input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-transparent transition-all border-gray-300"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-3 rounded-lg text-muted-foreground font-medium hover:bg-gray-100 transition-all"
            >
              Clear Form
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-6 py-3 rounded-lg text-white font-medium transition-all ${
                isSubmitting
                  ? 'bg-green-400'
                  : 'bg-green-600 hover:bg-green-700'
              } flex items-center justify-center space-x-2`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Updating Course...</span>
                </>
              ) : (
                <>
                  <BookOpen className="w-5 h-5" />
                  <span>Update Course</span>
                </>
              )}
            </button>
          </div>

          {/* Status Messages */}
          {isSuccess && (
            <div className="flex items-center justify-center text-green-600 bg-green-50 p-4 rounded-lg mt-4">
              <CheckCircle className="w-5 h-5 mr-2" />
              <span>Course updated successfully!</span>
            </div>
          )}
          {isError && (
            <div className="flex items-center justify-center text-red-600 bg-red-50 p-4 rounded-lg mt-4">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>Error updating course, please try again.</span>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default UpdatedCourse;
